import {
  Box,
  Button,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Td,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";

import React from "react";
import { useNavigate } from "react-router-dom";
import useActions from "../../hooks/useActions";
import {
  changeUploadRequest,
  deleteMovie,
  getAllUsers,
  updateMovie,
  userSearch,
} from "../../utils/api";
import useStore from "../../utils/store";
import Buttons2 from "../common/Actions/Buttons2";
import CreateModal from "../common/Modal/Modal";
import PreviewModal from "../common/Modal/PreviewModal";

import CreateTable from "../common/Table/Table";
import Loading from "../common/ui/Loading";
import TableSub from "../subscription/Table";
import IncreaseVotes from "../common/IncreaseVotes";

const Table2 = ({
  allPodcasts,
  series,
  actions,
  reload,
  updatedSeriesPodcast,
  deleteSeriesPodcast,
  episeries,
  categories,
  subcategories,
}) => {
  const user = useStore((state) => state.user);
  const nav = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = React.useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [data, setData] = React.useState([]);
  const [typeId, setTypeId] = React.useState("");

  // ====================== ASSIGN UPLOADER MODAL ======================
  const {
    isOpen: isAssignOpen,
    onOpen: onAssignOpen,
    onClose: onAssignClose,
  } = useDisclosure();

  const [assignMovieId, setAssignMovieId] = React.useState("");
  const [userSearchInput, setUserSearchInput] = React.useState(""); // renamed to avoid conflict
  const [assignUsers, setAssignUsers] = React.useState([]);
  const [assignUsersLoading, setAssignUsersLoading] = React.useState(false);
  const [assignPage, setAssignPage] = React.useState(1);
  const [hasMoreUsers, setHasMoreUsers] = React.useState(true);
  const [assignLoading, setAssignLoading] = React.useState(false);

  const searchTimeoutRef = React.useRef(null);

  // Determine which field the user-search API should query based on the input.
  const getUserSearchType = (query) => {
    const q = (query || "").trim();
    if (q.includes("@")) return "Email";
    if (/^[+\d][\d\s-]*$/.test(q)) return "Phone";
    return "Name";
  };

  // Server-side fetch using the userSearch API (empty query → paginated all users)
  const fetchAssignUsers = async (search = "", page = 1, reset = false) => {
    setAssignUsersLoading(true);
    try {
      const query = search.trim();
      const [success, response] = query
        ? await userSearch({
            limit: 30,
            page: page,
            searchType: getUserSearchType(query),
            searchQuery: query,
          })
        : await getAllUsers(page, 30);

      if (success) {
        const newUsers = response?.users || response?.data?.users || [];
        const totalData = response?.totalData ?? response?.data?.totalData ?? 0;
        const loadedCount = (reset ? 0 : assignUsers.length) + newUsers.length;

        if (reset) {
          setAssignUsers(newUsers);
        } else {
          setAssignUsers((prev) => [...prev, ...newUsers]);
        }
        setHasMoreUsers(
          totalData ? loadedCount < totalData : newUsers.length === 30
        );
        setAssignPage(page);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast({
        title: "Error",
        description: "Failed to load users",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setAssignUsersLoading(false);
    }
  };

  const openAssignModal = (movieId) => {
    setAssignMovieId(movieId);
    setUserSearchInput("");
    setAssignPage(1);
    setAssignUsers([]);
    setHasMoreUsers(true);
    onAssignOpen();

    // Load first page immediately (empty search = recent/popular users)
    fetchAssignUsers("", 1, true);
  };

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setUserSearchInput(value);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      setAssignPage(1);
      fetchAssignUsers(value, 1, true);
    }, 450);
  };

  const loadMoreUsers = () => {
    if (!assignUsersLoading && hasMoreUsers) {
      fetchAssignUsers(userSearchInput, assignPage + 1, false);
    }
  };

  const handleAssignUser = async (userId) => {
    setAssignLoading(true);
    const [success] = await changeUploadRequest(assignMovieId, userId);
    setAssignLoading(false);

    if (success) {
      toast({
        title: "User Assigned",
        description: "Movie uploader updated successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onAssignClose();
      reload();
    } else {
      toast({
        title: "Error",
        description: "Failed to assign user.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  React.useEffect(() => {
    if (!isAssignOpen) {
      setAssignUsers([]);
      setAssignPage(1);
      setHasMoreUsers(true);
    }
  }, [isAssignOpen]);

  const [previewUrl, setPreviewUrl] = React.useState("");
  const [previewTitle, setPreviewTitle] = React.useState("Preview");
  const [previewUrlType, setPreviewUrlType] = React.useState("");
  const {
    isOpen: isPreviewOpen,
    onOpen: onPreviewOpen,
    onClose: onPreviewClose,
  } = useDisclosure();

  const handlePreview = (url, title, urlType) => {
    setPreviewUrl(url);
    setPreviewTitle(title);
    setPreviewUrlType(urlType);
    onPreviewOpen();
  };

  const actionsI =
    actions && (user?.updateMovie || user?.deleteMovie) ? "Actions" : false;

  const [updateActionHandler, deleteAction] = useActions();

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <PreviewModal
        isOpen={isPreviewOpen}
        onClose={onPreviewClose}
        url={previewUrl}
        title={previewTitle}
        urlType={previewUrlType}
      />

      <CreateTable
        searchby={
          !episeries && [
            "name",
            "language",
            "ageLimit",
            "rating",
            "subcategory",
          ]
        }
        subcategories={subcategories}
        pagging={episeries ? false : true}
        head={[
          actionsI,
          "Name",
          !episeries && "Views",
          !episeries && "Votes",
          "Description",
          !categories && "Subscription",
          "Url",
          "Promotional Video",
          "Trailer",
          "Horizontal-Banner",
          "Vertical-Banner",
          !series && "Duration",
          !series && !episeries && "Subcategory",

          !episeries && "Rating",
          !episeries && "Language",
          !episeries && "Age-Limit",
          !episeries && "Cast",
          !episeries && "extraCast",
          !episeries && "Genre",
          !episeries && "Uploaded By",
        ]}
        data={allPodcasts}
        rows={(item) => {
          return (
            <>
              {actions && (user?.updateMovie || user?.deleteMovie) && (
                <Td>
                  {!episeries && (
                    <Button
                      colorScheme={item.isEnable ? "red" : "green"}
                      variant="solid"
                      onClick={async () => {
                        if (!series) {
                          await updateActionHandler(
                            {
                              isEnable: !item.isEnable,
                            },
                            item._id,
                            updateMovie
                          );

                          reload();
                        }
                      }}
                      sx={{
                        marginTop: "10px",
                        width: "100%",
                      }}
                    >
                      {item.isEnable ? "Disable" : "Enable"}
                    </Button>
                  )}
                  <Buttons2
                    episeries={episeries}
                    item={item}
                    update={async (data, id) => {
                      if (!series && !episeries) {
                        setLoading(true);
                        await updateActionHandler(data, id, updateMovie);
                        reload();
                        setLoading(false);
                      } else {
                        updatedSeriesPodcast(data, id);
                      }
                    }}
                    deletePodcast={async () => {
                      if (!series && !episeries) {
                        setLoading(true);
                        await deleteAction(item._id, deleteMovie);
                        reload();
                        setLoading(false);
                      } else {
                        deleteSeriesPodcast(item._id);
                      }
                    }}
                    subcategories={subcategories}
                  />{" "}
                </Td>
              )}
              <Td>{item?.name}</Td>
              {!episeries && <Td>{item?.view}</Td>}
              {!episeries && (
                <Td>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <IncreaseVotes
                      item={item}
                      reload={reload}
                    />
                    <Button
                      colorScheme="pink"
                      variant="solid"
                      size="sm"
                      onClick={() => nav(`/movies/likes/${item._id}`)}
                    >
                      View Votes
                    </Button>
                  </Box>
                </Td>
              )}
              <Td>{item?.description.slice(0, 25)}...</Td>{" "}
              {!categories && (
                <Td>
                  <Button
                    colorScheme={"blue"}
                    onClick={() => {
                      onOpen();
                      setData(item.subscriptions);
                      setTypeId(item._id);
                    }}
                  >
                    View
                  </Button>
                </Td>
              )}
              <Td>
                <Button
                  colorScheme={"blue"}
                  variant={"link"}
                  onClick={() => {
                    handlePreview(
                      item?.url,
                      "Main Content Preview",
                      item?.urlType
                    );
                  }}
                >
                  Preview
                </Button>
              </Td>
              <Td>
                <Button
                  colorScheme={"blue"}
                  variant={"link"}
                  onClick={() => {
                    handlePreview(
                      item?.promotionalLink,
                      "Promotional Content Preview",
                      item?.promotionalLinkType || "video"
                    );
                  }}
                >
                  Preview
                </Button>
              </Td>
              <Td>
                <Button
                  colorScheme={"blue"}
                  variant={"link"}
                  onClick={() => {
                    handlePreview(
                      item?.trailer,
                      "Trailer Preview",
                      item?.trailerType
                    );
                  }}
                >
                  Preview
                </Button>
              </Td>
              <Td>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                  }}
                >
                  <Image
                    src={item?.horizontalBanner}
                    alt="horizontal-banner"
                    sx={{
                      width: "100px",
                      height: "100px",
                    }}
                    rounded="md"
                  />
                  <Button
                    size="sm"
                    colorScheme="blue"
                    variant="outline"
                    onClick={() =>
                      handlePreview(
                        item?.horizontalBanner,
                        "Horizontal Banner Preview",
                        "image"
                      )
                    }
                  >
                    Preview
                  </Button>
                </Box>
              </Td>
              <Td>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                  }}
                >
                  <Image
                    src={item?.verticalBanner}
                    alt="vertical-banner"
                    sx={{
                      width: "100px",
                      height: "100px",
                    }}
                    rounded="md"
                  />
                  <Button
                    size="sm"
                    colorScheme="blue"
                    variant="outline"
                    onClick={() =>
                      handlePreview(
                        item?.verticalBanner,
                        "Vertical Banner Preview",
                        "image"
                      )
                    }
                  >
                    Preview
                  </Button>
                </Box>
              </Td>
              {!series && <Td>{item?.duration}</Td>}
              {!series && !episeries && <Td>{item?.subcategory}</Td>}
              {!episeries && (
                <>
                  <Td>{item?.rating}</Td>
                  <Td>{item?.language}</Td>
                  <Td>{item?.ageLimit}</Td>

                  <Td>
                    {item?.cast?.map((castItem, index) => {
                      return <p key={index}>{castItem.name}</p>;
                    })}
                  </Td>
                  <Td>{item?.extraCast}</Td>
                  <Td>
                    {item?.genres?.map((genreItem, index) => {
                      return <p key={index}>{genreItem.name}</p>;
                    })}
                  </Td>
                  <Td>
                    <Box display="flex" flexDirection="column" gap="0.4rem" minW="120px">
                      <Text fontSize="sm" fontWeight="medium">
                        {item?.uploadedBy ? (
                          <>
                            {item?.uploadedBy?.name}
                            <br />
                            {item?.uploadedBy?.email || ""}
                            <br />
                            {item?.uploadedBy?.phoneNumber || ""}
                          </>
                        ) : (
                          "Not assigned"
                        )}
                      </Text>
                      {actions && user?.updateMovie && (
                        <Button
                          size="xs"
                          colorScheme="purple"
                          variant="outline"
                          onClick={() => openAssignModal(item._id)}
                        >
                          {item?.uploadedBy ? "Change" : "Assign"}
                        </Button>
                      )}
                    </Box>
                  </Td>
                </>
              )}
            </>
          );
        }}
      />

      <CreateModal
        isOpen={isOpen}
        onClose={() => {
          setData([]);
          setTypeId("");
          onClose();
        }}
        title={"View Subscribtions"}
        table
      >
        <TableSub
          subscribtions={data}
          type="Movie"
          typeId={typeId}
          reload={reload}
          onClose={onClose}
        />
      </CreateModal>

      <Modal isOpen={isAssignOpen} onClose={onAssignClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Assign Uploader</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Input
              placeholder="Search by name, email or phone..."
              value={userSearchInput}
              onChange={handleSearchInput}
              mb={4}
            />

            {assignUsersLoading && assignUsers.length === 0 ? (
              <Box display="flex" justifyContent="center" py={8}>
                <Spinner />
              </Box>
            ) : (
              <>
                <VStack
                  align="stretch"
                  maxH="420px"
                  overflowY="auto"
                  spacing={2}
                >
                  {assignUsers.length === 0 ? (
                    <Text color="gray.500" textAlign="center" py={8}>
                      {userSearchInput.trim() ? "No users found" : "Type to search users..."}
                    </Text>
                  ) : (
                    assignUsers.map((u) => (
                      <Box
                        key={u._id}
                        p={3}
                        borderRadius="md"
                        border="1px solid"
                        borderColor="gray.200"
                        _hover={{ bg: "purple.50", borderColor: "purple.300", cursor: "pointer" }}
                        onClick={() => !assignLoading && handleAssignUser(u._id)}
                      >
                        <Text fontWeight="semibold" fontSize="sm">
                          {u.name || "—"}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {u.email}{" "}
                          {u.phoneNumber || u.phone
                            ? `• ${u.phoneNumber || u.phone}`
                            : ""}
                        </Text>
                      </Box>
                    ))
                  )}
                </VStack>

                {hasMoreUsers && (
                  <Button
                    mt={4}
                    width="full"
                    variant="outline"
                    onClick={loadMoreUsers}
                    isLoading={assignUsersLoading}
                  >
                    Load More Users
                  </Button>
                )}
              </>
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              onClick={onAssignClose}
              isDisabled={assignLoading}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default Table2;